// src/components/Budget/BudgetSuggestionCard.js
// A glass-morphism AI suggestion card that sits at the top of the budget page.
// Analyses the user's budget categories and surfaces smart recommendations.
// Dismissable — state persisted to localStorage.

import React, { useState, useMemo, useCallback } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Chip,
  useTheme,
  alpha,
  Collapse,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { useTranslation } from 'react-i18next';

// ──────────────────────────────────────────────
// localStorage helpers
// ──────────────────────────────────────────────
const DISMISS_KEY = 'soldikeeper_budget_suggestion_dismissed';

const isDismissed = () => {
  try {
    const stored = localStorage.getItem(DISMISS_KEY);
    if (!stored) return false;
    const { timestamp } = JSON.parse(stored);
    // Auto-reset dismissal after 7 days so fresh suggestions surface
    const SEVEN_DAYS = 7 * 24 * 60 * 60 * 1000;
    return Date.now() - timestamp < SEVEN_DAYS;
  } catch {
    return false;
  }
};

const setDismissed = () => {
  try {
    localStorage.setItem(
      DISMISS_KEY,
      JSON.stringify({ timestamp: Date.now() })
    );
  } catch {
    // storage full or blocked — ignore
  }
};

// ──────────────────────────────────────────────
// Suggestion generator
// ──────────────────────────────────────────────
const generateSuggestion = (categories, budgetMetrics, t) => {
  // 1. No budgets at all
  if (!categories || categories.length === 0) {
    return {
      id: 'no-budgets',
      message: t(
        'budgetSuggestions.noBudgets',
        'Set up your first budget category to start tracking your spending! Budgets help you stay in control of your finances.'
      ),
      priority: 1,
    };
  }

  // 2. Very few categories
  if (categories.length < 3) {
    return {
      id: 'few-categories',
      message: t(
        'budgetSuggestions.fewCategories',
        'You only have {{count}} budget category. Creating more categories gives you finer control over where your money goes.',
        { count: categories.length }
      ),
      priority: 2,
    };
  }

  // 3. Overall overspending
  if (
    budgetMetrics &&
    budgetMetrics.totalBudget > 0 &&
    budgetMetrics.totalSpent > budgetMetrics.totalBudget
  ) {
    return {
      id: 'overall-over',
      message: t(
        'budgetSuggestions.overallOver',
        'You\'ve spent more than your total budget this month. Consider reviewing individual categories to find areas where you can cut back.'
      ),
      priority: 3,
    };
  }

  // 4. Has a lot of room — encourage the user
  if (
    budgetMetrics &&
    budgetMetrics.totalBudget > 0 &&
    budgetMetrics.percentUsed < 40
  ) {
    return {
      id: 'well-under',
      message: t(
        'budgetSuggestions.wellUnder',
        'You\'re well within your budgets — great discipline! Consider putting the surplus into savings or an emergency fund.'
      ),
      priority: 5,
    };
  }

  // 5. Generic actionable tip
  return {
    id: 'generic',
    message: t(
      'budgetSuggestions.generic',
      'Review your budgets regularly. Small adjustments each month help you build better spending habits over time.'
    ),
    priority: 6,
  };
};

// ──────────────────────────────────────────────
// Component
// ──────────────────────────────────────────────
const BudgetSuggestionCard = ({ categories, budgetMetrics }) => {
  const theme = useTheme();
  const { t } = useTranslation();

  // Dismissal state — initialise from localStorage
  const [dismissed, setDismissedState] = useState(() => isDismissed());

  const suggestion = useMemo(
    () => generateSuggestion(categories, budgetMetrics, t),
    [categories, budgetMetrics, t]
  );

  const handleDismiss = useCallback(() => {
    setDismissedState(true);
    setDismissed();
  }, []);

  // AI accent colours
  const purpleAccent = '#8b5cf6';
  const cyanAccent = '#06b6d4';
  const isDark = theme.palette.mode === 'dark';

  return (
    <Collapse in={!dismissed} timeout={400}>
      <Box
        sx={{
          position: 'relative',
          mb: { xs: 2, sm: 2.5 },
          mx: { xs: -1, sm: 0 },
          borderRadius: { xs: 0, sm: 3 },
          overflow: 'hidden',
          // Glass-morphism
          backdropFilter: 'blur(16px)',
          WebkitBackdropFilter: 'blur(16px)',
          bgcolor: isDark
            ? alpha('#1e1b4b', 0.55) // deep indigo base
            : alpha('#f5f3ff', 0.75),
          // Gradient border via box-shadow trick
          boxShadow: `inset 0 0 0 1px ${alpha(purpleAccent, 0.25)}, 0 4px 24px ${alpha(purpleAccent, 0.08)}`,
          // Subtle gradient overlay via pseudo-element would need styled(),
          // so we use a top border as a simpler accent
          borderTop: `2px solid`,
          borderImage: `linear-gradient(90deg, ${purpleAccent}, ${cyanAccent}) 1`,
        }}
      >
        <Box sx={{ p: { xs: 2, sm: 2.5 } }}>
          {/* ── Header row ── */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              mb: 1.25,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <AutoAwesomeIcon
                sx={{
                  fontSize: '1.15rem',
                  color: purpleAccent,
                  filter: `drop-shadow(0 0 4px ${alpha(purpleAccent, 0.4)})`,
                }}
              />
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: 700,
                  fontSize: '0.82rem',
                  background: `linear-gradient(135deg, ${purpleAccent}, ${cyanAccent})`,
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                  letterSpacing: 0.3,
                }}
              >
                {t('budgetSuggestions.header', '🤖 AI Suggestion')}
              </Typography>
            </Box>
            <IconButton
              size="small"
              onClick={handleDismiss}
              aria-label={t('budgetSuggestions.dismiss', 'Dismiss suggestion')}
              sx={{
                p: 0.5,
                color: 'text.disabled',
                '&:hover': { color: 'text.secondary' },
              }}
            >
              <CloseIcon sx={{ fontSize: '1rem' }} />
            </IconButton>
          </Box>

          {/* ── Suggestion body ── */}
          <Typography
            variant="body2"
            sx={{
              fontSize: '0.82rem',
              lineHeight: 1.6,
              color: 'text.primary',
              mb: 1.5,
            }}
          >
            {suggestion.message}
          </Typography>

          {/* ── Footer ── */}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Chip
              icon={
                <AutoAwesomeIcon
                  sx={{
                    fontSize: '0.7rem !important',
                    color: `${purpleAccent} !important`,
                  }}
                />
              }
              label={t(
                'budgetSuggestions.poweredBy',
                'Powered by SoldiKeeper AI'
              )}
              size="small"
              variant="outlined"
              sx={{
                height: 22,
                fontSize: '0.6rem',
                fontWeight: 500,
                borderColor: alpha(purpleAccent, 0.25),
                color: isDark
                  ? alpha(purpleAccent, 0.8)
                  : purpleAccent,
                '& .MuiChip-icon': { ml: '4px' },
              }}
            />
          </Box>
        </Box>
      </Box>
    </Collapse>
  );
};

export default BudgetSuggestionCard;
